import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";

import {
  customerInfo,
  listCustomerUsers,
  listEndUsers,
  createCustomerUser,
  createLaunchUrl,
  inviteEndUser,
  getInvitationStats,
  deleteEndUser,
  setEndUserType,
  bulkInviteEndUsers,
  BulkInviteResult,
  resendInvite,
  resetUserOnboarding,
  regenerateApiKey,
} from "@/services/adminDashboard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ToastAction } from "@/components/ui/toast";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { logoutAll } from "@/services/adminCustomerAuth";
import { sendLoginLink, requestPasswordReset } from "@/services/auth";
import { useBranding } from "@/hooks/useBranding";
import BrandLogo from "../assets/golfrecruit-lockup-light.svg";
import { DESIGN_COLORS, DESIGN_SPACING, DESIGN_SIZING } from "@/constants/design";
import {
  CARD_CLASSES,
  INPUT_CLASSES,
  BUTTON_PRIMARY_CLASSES,
  INFO_BOX_CLASSES,
  INFO_BOX_ALT_CLASSES,
} from "@/constants/tailwind";

type CustomerMeta = {
  id: string;
  name: string;
  isActive?: boolean;
  createdAt?: string;
  lastUsed?: string;
  apiKeyPrefix?: string;
};
type CustomerUser = { id: string; email: string; createdAt?: string };
type EndUser = {
  _id?: string;
  id?: string;
  email?: string;
  hasJgsMapping?: boolean;
  hasPassword?: boolean;
  acceptedMagicLink?: boolean;
  isInvited?: boolean;
  userType?: "player" | "coach";
  linkedProfileCount?: number;
  jgsData?: {
    player_name?: string;
    state?: string;
    class_year?: string;
    rank?: number;
    scoring_differential?: number;
  };
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { branding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<CustomerMeta | null>(null);
  const [customerUsers, setCustomerUsers] = useState<CustomerUser[]>([]);
  const [endUsers, setEndUsers] = useState<EndUser[]>([]);
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [launchingFor, setLaunchingFor] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [invitationStats, setInvitationStats] = useState<{
    currentCount: number;
    maxUsers: number;
    canInvite: boolean;
  } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState<string | null>(null);
  const [settingType, setSettingType] = useState<string | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvEmails, setCsvEmails] = useState<string[]>([]);
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [bulkInviting, setBulkInviting] = useState(false);
  const [bulkResults, setBulkResults] = useState<BulkInviteResult[] | null>(null);
  const [resending, setResending] = useState<string | null>(null);
  const [sendingLoginLink, setSendingLoginLink] = useState<string | null>(null);
  const [loginLinkConfirm, setLoginLinkConfirm] = useState<EndUser | null>(null);
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [newFullKey, setNewFullKey] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState<{
    active: number;
    invited: number;
    lapsed: number;
    total: number;
  }>({ active: 0, invited: 0, lapsed: 0, total: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [m, cu, euRes, stats] = await Promise.all([
          customerInfo(),
          listCustomerUsers(),
          listEndUsers(),
          getInvitationStats(),
        ]);
        setMeta(m);
        setCustomerUsers(cu);
        setEndUsers(euRes.users);
        setStatusCounts(euRes.statusCounts);
        setInvitationStats(stats);
      } catch (err: any) {
        toast({
          title: "Failed to load customer data",
          description: String(err?.message || err),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  if (loading) {
    return <div className="p-6">Loading…</div>;
  }

  async function handleLogout() {
    try {
      try {
        await logoutAll();
      } catch {}
      navigate("/", { replace: true });
    } catch {
      navigate("/", { replace: true });
    }
  }

  return (
    <div className="bg-surface flex flex-col min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-[rgba(17,31,17,0.1)] shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] px-8 py-0 h-[89px] flex items-center justify-between">
        {/* Logo + title */}
        <div className="h-[56px] flex flex-col justify-center">
          <img src={BrandLogo} alt="golfrecruit.ai" className="h-8 w-auto mb-1" />
          <span className="text-xs text-muted-foreground ml-2">Admin Dashboard</span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 bg-white rounded-lg border px-6 py-2 text-forest hover:bg-gray-50"
        >
          Logout
        </button>
      </div>
      {/* Welcome, description */}
      <div className="bg-gradient-to-b from-primary to-primary/80 py-6 px-8">
        <h1 className="text-2xl font-medium text-primary-foreground mb-1 tracking-[0.07px]">
          Welcome to your Admin Dashboard
        </h1>
        <p className="text-primary-foreground/90 text-base">
          Manage customer accounts, configure login settings, and track end user activity all in one
          place.
        </p>
      </div>
      <div className="flex-1 flex flex-col gap-6 p-8">
        {/* CARD: Customer Info Section */}
        <div className={CARD_CLASSES}>
          {meta && (
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="text-forest font-semibold text-base">{meta.name}</div>
                <div className="bg-lime-soft h-[28px] rounded-full px-3 flex items-center gap-2">
                  <div className="bg-primary rounded-full size-[8px]" />
                  <span className="text-primary-text text-sm">Active</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 mb-6">
                <div className="flex flex-col gap-1">
                  <div className="text-sm text-muted-foreground">Customer ID:</div>
                  <div className="text-sm text-forest">{meta.id}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-sm text-muted-foreground">Name:</div>
                  <div className="text-base text-forest font-semibold">{meta.name}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-sm text-muted-foreground">Status:</div>
                  <div className="text-base text-forest font-semibold">
                    {meta.isActive !== false ? "Active" : "Inactive"}
                  </div>
                </div>
                {meta.createdAt ? (
                  <div className="flex flex-col gap-1">
                    <div className="text-sm text-muted-foreground">Created:</div>
                    <div className="text-base text-forest font-semibold">
                      {new Date(meta.createdAt).toLocaleString()}
                    </div>
                  </div>
                ) : null}
                {meta.lastUsed ? (
                  <div className="flex flex-col gap-1">
                    <div className="text-sm text-muted-foreground">Last Used:</div>
                    <div className="text-base text-forest font-semibold">
                      {new Date(meta.lastUsed).toLocaleString()}
                    </div>
                  </div>
                ) : null}
                <div className="flex flex-col gap-1">
                  <div className="text-sm text-muted-foreground">API Key:</div>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-forest font-mono">
                      {meta.apiKeyPrefix ? `${meta.apiKeyPrefix}${"••••••"}` : "No key generated"}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRegenConfirmOpen(true)}
                      disabled={regenerating}
                    >
                      {meta.apiKeyPrefix ? "Regenerate Key" : "Generate Key"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* CARD: Admin Users */}
        <div className={CARD_CLASSES}>
          <div className="mb-2">
            <div className="text-forest font-semibold text-base mb-1">Admin Users</div>
            <span className="text-sm text-muted-foreground">Add and Remove administrators</span>
          </div>
          <div
            className={`${INFO_BOX_ALT_CLASSES} p-4 text-muted-foreground text-sm font-medium mb-4`}
          >
            <span className="font-semibold">Quick Start</span>: Add email addresses here to grant
            admin access to this customer account. Admin users will have access to this dashboard to
            manage accounts, settings, and users.
          </div>
          <form
            className="flex items-center gap-2 mb-4"
            onSubmit={async e => {
              e.preventDefault();
              if (!newEmail) return;
              try {
                setCreating(true);
                const res = await createCustomerUser(newEmail);
                setNewEmail("");
                // Refresh list
                const cu = await listCustomerUsers();
                setCustomerUsers(cu);
                toast({
                  title: "Customer user created",
                  description: (
                    <div className="flex items-center gap-2">
                      <span>Temporary password:</span>
                      <code className="rounded bg-muted px-1 py-0.5 font-mono">
                        {res.temporaryPassword}
                      </code>
                    </div>
                  ),
                  action: (
                    <ToastAction
                      altText="Copy password"
                      onClick={async () => {
                        try {
                          if (navigator?.clipboard?.writeText) {
                            await navigator.clipboard.writeText(res.temporaryPassword);
                          } else {
                            const ta = document.createElement("textarea");
                            ta.value = String(res.temporaryPassword ?? "");
                            ta.style.position = "fixed";
                            ta.style.left = "-9999px";
                            document.body.appendChild(ta);
                            ta.focus();
                            ta.select();
                            try {
                              document.execCommand("copy");
                            } catch {}
                            document.body.removeChild(ta);
                          }
                        } catch {}
                      }}
                    >
                      Copy
                    </ToastAction>
                  ),
                });
              } catch (err: any) {
                toast({
                  title: "Create failed",
                  description: String(err?.message || err),
                  variant: "destructive",
                });
              } finally {
                setCreating(false);
              }
            }}
          >
            <Input
              placeholder="email@example.com"
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              className={`max-w-xs ${INPUT_CLASSES}`}
            />
            <Button
              type="submit"
              disabled={creating || !newEmail}
              className={BUTTON_PRIMARY_CLASSES}
            >
              Add Admin
            </Button>
          </form>
          {customerUsers.length === 0 ? (
            <div className="text-sm text-muted-foreground">No admin users.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customerUsers.map(u => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.email}</TableCell>
                    <TableCell>
                      {u.createdAt ? new Date(u.createdAt).toLocaleString() : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* CARD: Invite End User */}
        <div className={CARD_CLASSES}>
          <div className="mb-2">
            <div className="text-forest font-semibold text-base mb-1">Invite End Users</div>
            <span className="text-sm text-muted-foreground">
              Invite new end users by email. They will receive an invitation to set up their
              password.
            </span>
          </div>
          {invitationStats && (
            <div className="mb-4 text-sm text-muted-foreground">
              {invitationStats.currentCount} / {invitationStats.maxUsers} users
              {!invitationStats.canInvite && (
                <span className="ml-2 text-destructive font-medium">(Limit reached)</span>
              )}
            </div>
          )}
          <Tabs defaultValue="single" className="w-full">
            <TabsList>
              <TabsTrigger value="single">Single Invite</TabsTrigger>
              <TabsTrigger value="csv">CSV Upload</TabsTrigger>
            </TabsList>
            <TabsContent value="single">
              <form
                className="flex items-center gap-2"
                onSubmit={async e => {
                  e.preventDefault();
                  if (!inviteEmail || !invitationStats?.canInvite) return;
                  try {
                    setInviting(true);
                    await inviteEndUser(inviteEmail);
                    setInviteEmail("");
                    toast({
                      title: "Invitation sent",
                      description: `An invitation email has been sent to ${inviteEmail}`,
                    });
                    const [stats, euRes] = await Promise.all([
                      getInvitationStats(),
                      listEndUsers(),
                    ]);
                    setInvitationStats(stats);
                    setEndUsers(euRes.users);
                    setStatusCounts(euRes.statusCounts);
                  } catch (err: any) {
                    toast({
                      title: "Invitation failed",
                      description: String(err?.message || err),
                      variant: "destructive",
                    });
                  } finally {
                    setInviting(false);
                  }
                }}
              >
                <Input
                  placeholder="email@example.com"
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  className={`max-w-xs ${INPUT_CLASSES}`}
                  disabled={inviting || !invitationStats?.canInvite}
                />
                <Button
                  type="submit"
                  disabled={inviting || !inviteEmail || !invitationStats?.canInvite}
                  className={BUTTON_PRIMARY_CLASSES}
                >
                  {inviting ? "Sending…" : "Invite"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="csv">
              {bulkResults ? (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground">
                    <span className="font-semibold">
                      {bulkResults.filter(r => r.status === "success").length}
                    </span>{" "}
                    invited successfully,{" "}
                    <span className="font-semibold">
                      {bulkResults.filter(r => r.status === "failed").length}
                    </span>{" "}
                    failed
                    {bulkResults.some(r => r.status === "email_failed") && (
                      <span className="text-amber-600 font-medium">
                        , {bulkResults.filter(r => r.status === "email_failed").length} email
                        {bulkResults.filter(r => r.status === "email_failed").length !== 1
                          ? "s"
                          : ""}{" "}
                        failed to send
                      </span>
                    )}
                  </div>
                  {bulkResults.some(r => r.status === "email_failed") && (
                    <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-3">
                      Some users were created but their invitation emails failed to send. Re-upload
                      a CSV with just those emails to retry.
                    </div>
                  )}
                  <div className="max-h-64 overflow-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bulkResults.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{r.email}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  r.status === "success"
                                    ? "default"
                                    : r.status === "email_failed"
                                      ? "outline"
                                      : "destructive"
                                }
                              >
                                {r.status === "success"
                                  ? "Invited"
                                  : r.status === "email_failed"
                                    ? "Email Failed"
                                    : "Failed"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {r.error || "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setCsvFile(null);
                      setCsvEmails([]);
                      setCsvErrors([]);
                      setBulkResults(null);
                    }}
                  >
                    Upload another CSV
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          const input = document.getElementById(
                            "csv-file-input"
                          ) as HTMLInputElement;
                          input?.click();
                        }}
                      >
                        Choose CSV File
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {csvFile ? csvFile.name : "No file selected"}
                      </span>
                    </div>
                    <input
                      id="csv-file-input"
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0] || null;
                        setCsvFile(file);
                        setCsvEmails([]);
                        setCsvErrors([]);
                        setBulkResults(null);
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = ev => {
                          const text = ev.target?.result as string;
                          if (!text || !text.trim()) {
                            setCsvErrors(["File is empty"]);
                            return;
                          }
                          const lines = text
                            .split(/\r?\n/)
                            .map(l => l.trim())
                            .filter(Boolean);
                          const headerPatterns = [
                            "email",
                            "emails",
                            "email address",
                            "email_address",
                            "emailaddress",
                          ];
                          let startIndex = 0;
                          if (lines.length > 0) {
                            const firstLine = lines[0]
                              .replace(/^["']|["']$/g, "")
                              .trim()
                              .toLowerCase();
                            if (headerPatterns.includes(firstLine)) {
                              startIndex = 1;
                            }
                          }
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          const seen = new Set<string>();
                          const valid: string[] = [];
                          const errors: string[] = [];
                          let duplicateCount = 0;
                          for (let i = startIndex; i < lines.length; i++) {
                            // Handle quoted values
                            let value = lines[i];
                            if (
                              (value.startsWith('"') && value.endsWith('"')) ||
                              (value.startsWith("'") && value.endsWith("'"))
                            ) {
                              value = value.slice(1, -1);
                            }
                            // Handle comma-separated rows (take first column)
                            if (value.includes(",")) {
                              value = value
                                .split(",")[0]
                                .replace(/^["']|["']$/g, "")
                                .trim();
                            }
                            const normalized = value.toLowerCase().trim();
                            if (!normalized) continue;
                            if (seen.has(normalized)) {
                              duplicateCount++;
                              continue;
                            }
                            seen.add(normalized);
                            if (emailRegex.test(normalized)) {
                              valid.push(normalized);
                            } else {
                              errors.push(`Row ${i + 1}: invalid email "${value}"`);
                            }
                          }
                          if (duplicateCount > 0) {
                            errors.unshift(
                              `${duplicateCount} duplicate email${duplicateCount > 1 ? "s" : ""} removed`
                            );
                          }
                          setCsvEmails(valid);
                          setCsvErrors(errors);
                        };
                        reader.readAsText(file);
                      }}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Upload a CSV file with one email per row. A header row labeled "email" will be
                      auto-skipped.
                    </p>
                  </div>
                  {csvErrors.length > 0 && (
                    <div className="text-sm space-y-1">
                      {csvErrors.map((err, i) => (
                        <div key={i} className="text-amber-600">
                          {err}
                        </div>
                      ))}
                    </div>
                  )}
                  {csvEmails.length > 0 && (
                    <>
                      <div className="text-sm text-muted-foreground">
                        <span className="font-semibold">{csvEmails.length}</span> valid email
                        {csvEmails.length !== 1 ? "s" : ""} found
                        {invitationStats &&
                          csvEmails.length >
                            invitationStats.maxUsers - invitationStats.currentCount && (
                            <span className="ml-2 text-amber-600 font-medium">
                              (only{" "}
                              {Math.max(0, invitationStats.maxUsers - invitationStats.currentCount)}{" "}
                              slots available)
                            </span>
                          )}
                      </div>
                      <div className="max-h-40 overflow-auto border rounded-md p-2 text-sm font-mono">
                        {csvEmails.map((email, i) => (
                          <div key={i}>{email}</div>
                        ))}
                      </div>
                      <Button
                        disabled={bulkInviting || !invitationStats?.canInvite}
                        className={BUTTON_PRIMARY_CLASSES}
                        onClick={async () => {
                          try {
                            setBulkInviting(true);
                            const response = await bulkInviteEndUsers(csvEmails);
                            setBulkResults(response.results);
                            toast({
                              title: "Bulk invite complete",
                              description: `${response.successCount} invited, ${response.failedCount} failed`,
                            });
                            const [stats, euRes] = await Promise.all([
                              getInvitationStats(),
                              listEndUsers(),
                            ]);
                            setInvitationStats(stats);
                            setEndUsers(euRes.users);
                            setStatusCounts(euRes.statusCounts);
                          } catch (err: any) {
                            toast({
                              title: "Bulk invite failed",
                              description: String(err?.message || err),
                              variant: "destructive",
                            });
                          } finally {
                            setBulkInviting(false);
                          }
                        }}
                      >
                        {bulkInviting
                          ? "Inviting…"
                          : `Invite ${csvEmails.length} user${csvEmails.length !== 1 ? "s" : ""}`}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* CARD: Users (End Users) */}
        <div className={CARD_CLASSES}>
          <div className="mb-2">
            <div className="text-forest font-semibold text-base mb-1">Users</div>
            <span className="text-sm text-muted-foreground">
              Manage and impersonate golfrecruit.ai users
            </span>
          </div>
          {invitationStats && (
            <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
              <span>
                {invitationStats.currentCount} / {invitationStats.maxUsers} users
              </span>
              {endUsers.length > 0 && (
                <>
                  <span className="text-border-strong">|</span>
                  <Badge variant="default">{statusCounts.active} Active</Badge>
                  <Badge variant="secondary">{statusCounts.invited} Invited</Badge>
                  <Badge variant="outline">{statusCounts.lapsed} Lapsed</Badge>
                </>
              )}
            </div>
          )}
          <div className={`${INFO_BOX_CLASSES} p-4 text-muted-foreground text-sm font-medium mb-4`}>
            <span className="font-semibold">About End Users:</span> Below is the list of users that
            have access to golfrecruit.ai.
            <br />
            If a user has linked a JGS profile to their account, you will see JGS data including
            Player Name, Ranking and Scoring Differential.
            <br />
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>
                <span className="font-semibold">Unlink</span> - click this button to unlink the
                existing JGS profile from a user. This is useful if a user linked the wrong JGS
                profile or if the user is a coach that wants to switch JGS profiles to help
                different students.
              </li>
              <li>
                <span className="font-semibold">Launch</span> - Click launch to impersonate a user.
                It will launch the golfrecruit.ai application and log you in as that user so you can
                see exactly what they've been doing in golfrecruit.ai.
              </li>
            </ul>
          </div>
          {endUsers.length === 0 ? (
            <div className="text-sm text-muted-foreground">No end users.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Activation</TableHead>
                  <TableHead>Player</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Rank</TableHead>
                  <TableHead>Diff</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {endUsers.map((u, idx) => (
                  <TableRow key={u.id || u._id || String(idx)}>
                    <TableCell className="font-medium">{u.email || "-"}</TableCell>
                    <TableCell>
                      {u.isInvited ? (
                        <span className="text-muted-foreground">-</span>
                      ) : u.email ? (
                        <Select
                          value={u.userType || "player"}
                          onValueChange={async val => {
                            if (!u.email || val === (u.userType || "player")) return;
                            try {
                              setSettingType(u.email);
                              await setEndUserType(u.email, val as "player" | "coach");
                              const euRes = await listEndUsers();
                              setEndUsers(euRes.users);
                              setStatusCounts(euRes.statusCounts);
                              toast({
                                title: "Type updated",
                                description: `${u.email} is now a ${val}`,
                              });
                            } catch (err: any) {
                              toast({
                                title: "Update failed",
                                description: String(err?.message || err),
                                variant: "destructive",
                              });
                            } finally {
                              setSettingType(null);
                            }
                          }}
                          disabled={settingType === u.email}
                        >
                          <SelectTrigger className="w-[100px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="player">Player</SelectItem>
                            <SelectItem value="coach">Coach</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.isInvited ? (
                        <Badge variant="secondary">Invited</Badge>
                      ) : u.hasPassword || u.acceptedMagicLink ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="outline">Lapsed</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {(u.userType || "player") === "coach"
                        ? u.linkedProfileCount
                          ? `${u.linkedProfileCount} linked`
                          : "N/A"
                        : u.jgsData?.player_name || (u.hasJgsMapping ? "Linked" : "-")}
                    </TableCell>
                    <TableCell>
                      {(u.userType || "player") === "coach" ? "N/A" : u.jgsData?.state || "-"}
                    </TableCell>
                    <TableCell>
                      {(u.userType || "player") === "coach" ? "N/A" : u.jgsData?.class_year || "-"}
                    </TableCell>
                    <TableCell>
                      {(u.userType || "player") === "coach"
                        ? "N/A"
                        : typeof u.jgsData?.rank === "number"
                          ? u.jgsData?.rank
                          : "-"}
                    </TableCell>
                    <TableCell>
                      {(u.userType || "player") === "coach"
                        ? "N/A"
                        : typeof u.jgsData?.scoring_differential !== "undefined"
                          ? String(u.jgsData?.scoring_differential)
                          : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {(u.userType || "player") !== "coach" && u.email && !u.isInvited ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={unlinking === u.email}
                            onClick={async () => {
                              const ok = window.confirm(
                                `Reset onboarding for ${u.email}? This will unlink JGS and clear onboarding state.`
                              );
                              if (!ok) return;
                              try {
                                setUnlinking(u.email);
                                await resetUserOnboarding(u.email);
                                const euRes = await listEndUsers();
                                setEndUsers(euRes.users);
                                setStatusCounts(euRes.statusCounts);
                                toast({
                                  title: "Onboarding reset",
                                  description: `${u.email} will see the onboarding flow again`,
                                });
                              } catch (err: any) {
                                toast({
                                  title: "Reset failed",
                                  description: String(err?.message || err),
                                  variant: "destructive",
                                });
                              } finally {
                                setUnlinking(null);
                              }
                            }}
                          >
                            {unlinking === u.email ? "Resetting…" : "Reset"}
                          </Button>
                        ) : null}
                        {u.email && !u.isInvited ? (
                          <Button
                            variant="default"
                            size="sm"
                            disabled={launchingFor === u.email}
                            onClick={async () => {
                              try {
                                setLaunchingFor(u.email!);
                                const audience = window.location.origin;
                                const data = await createLaunchUrl(u.email!, audience);
                                if (!data?.launchUrl) {
                                  throw new Error("No launchUrl returned");
                                }
                                window.location.assign(data.launchUrl);
                              } catch (err: any) {
                                toast({
                                  title: "Launch failed",
                                  description: String(err?.message || err),
                                  variant: "destructive",
                                });
                              } finally {
                                setLaunchingFor(null);
                              }
                            }}
                          >
                            {launchingFor === u.email ? "Launching…" : "Launch"}
                          </Button>
                        ) : null}
                        {u.email && !u.isInvited && (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={sendingLoginLink === u.email}
                            onClick={() => setLoginLinkConfirm(u)}
                          >
                            {sendingLoginLink === u.email
                              ? "Sending…"
                              : u.hasPassword
                                ? "Reset Password"
                                : "Send Login Link"}
                          </Button>
                        )}
                        {u.isInvited && u.email && (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={resending === u.email}
                            onClick={async () => {
                              setResending(u.email!);
                              try {
                                await resendInvite(u.email!);
                                toast({
                                  title: "Invitation resent",
                                  description: `Email sent to ${u.email}`,
                                });
                              } catch (err: any) {
                                toast({
                                  title: "Resend failed",
                                  description: err.message,
                                  variant: "destructive",
                                });
                              } finally {
                                setResending(null);
                              }
                            }}
                          >
                            {resending === u.email ? "Sending…" : "Resend"}
                          </Button>
                        )}
                        {u.email ? (
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={deleting === u.email}
                            onClick={() => {
                              setDeleteConfirmEmail(u.email!);
                            }}
                          >
                            {deleting === u.email ? "Deleting…" : "Delete"}
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
      <Dialog
        open={!!deleteConfirmEmail}
        onOpenChange={open => {
          if (!open) setDeleteConfirmEmail(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete End User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deleteConfirmEmail}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmEmail(null)}
              disabled={!!deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!deleteConfirmEmail) return;
                try {
                  setDeleting(deleteConfirmEmail);
                  await deleteEndUser(deleteConfirmEmail);
                  toast({
                    title: "Deleted",
                    description: `${deleteConfirmEmail} has been deleted`,
                  });
                  setDeleteConfirmEmail(null);
                  // Refresh end users list and stats
                  const [euRes, stats] = await Promise.all([listEndUsers(), getInvitationStats()]);
                  setEndUsers(euRes.users);
                  setStatusCounts(euRes.statusCounts);
                  setInvitationStats(stats);
                } catch (err: any) {
                  toast({
                    title: "Delete failed",
                    description: String(err?.message || err),
                    variant: "destructive",
                  });
                } finally {
                  setDeleting(null);
                }
              }}
              disabled={!!deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Send Login Link / Reset Password Confirmation Dialog */}
      <Dialog
        open={!!loginLinkConfirm}
        onOpenChange={open => {
          if (!open) setLoginLinkConfirm(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {loginLinkConfirm?.hasPassword ? "Reset Password" : "Send Login Link"}
            </DialogTitle>
            <DialogDescription>
              {loginLinkConfirm?.hasPassword
                ? `This will send a password reset email to ${loginLinkConfirm?.email}. Their current password will continue to work until they set a new one.`
                : `This will send a login link to ${loginLinkConfirm?.email}. The link will expire in 7 days.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setLoginLinkConfirm(null)}
              disabled={!!sendingLoginLink}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              className={BUTTON_PRIMARY_CLASSES}
              disabled={!!sendingLoginLink}
              onClick={async () => {
                if (!loginLinkConfirm?.email) return;
                const email = loginLinkConfirm.email;
                const hasPassword = loginLinkConfirm.hasPassword;
                setSendingLoginLink(email);
                try {
                  if (hasPassword) {
                    await requestPasswordReset(email);
                    toast({ title: "Password reset sent", description: `Email sent to ${email}` });
                  } else {
                    await sendLoginLink(email);
                    toast({ title: "Login link sent", description: `Email sent to ${email}` });
                  }
                  setLoginLinkConfirm(null);
                } catch (err: any) {
                  toast({
                    title: "Failed to send email",
                    description: String(err?.message || err),
                    variant: "destructive",
                  });
                } finally {
                  setSendingLoginLink(null);
                }
              }}
            >
              {sendingLoginLink ? "Sending…" : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* API Key Regeneration Confirmation Dialog */}
      <Dialog
        open={regenConfirmOpen}
        onOpenChange={open => {
          if (!open) setRegenConfirmOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {meta?.apiKeyPrefix ? "Regenerate API Key" : "Generate API Key"}
            </DialogTitle>
            <DialogDescription>
              {meta?.apiKeyPrefix
                ? "The current API key will be immediately invalidated. Any integrations using it will stop working."
                : "A new API key will be generated for this customer."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRegenConfirmOpen(false)}
              disabled={regenerating}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              className={BUTTON_PRIMARY_CLASSES}
              disabled={regenerating}
              onClick={async () => {
                try {
                  setRegenerating(true);
                  const res = await regenerateApiKey();
                  setNewFullKey(res.apiKey);
                  setRegenConfirmOpen(false);
                  setMeta(prev => (prev ? { ...prev, apiKeyPrefix: res.apiKeyPrefix } : prev));
                } catch (err: any) {
                  toast({
                    title: "Failed to regenerate key",
                    description: String(err?.message || err),
                    variant: "destructive",
                  });
                } finally {
                  setRegenerating(false);
                }
              }}
            >
              {regenerating ? "Generating..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* One-time full API key display dialog */}
      <Dialog
        open={!!newFullKey}
        onOpenChange={open => {
          if (!open) setNewFullKey(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your New API Key</DialogTitle>
            <DialogDescription>Copy this key now. It will not be shown again.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
            <code className="text-sm font-mono flex-1 break-all">{newFullKey}</code>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  if (navigator?.clipboard?.writeText) {
                    await navigator.clipboard.writeText(newFullKey || "");
                  } else {
                    const ta = document.createElement("textarea");
                    ta.value = newFullKey || "";
                    ta.style.position = "fixed";
                    ta.style.left = "-9999px";
                    document.body.appendChild(ta);
                    ta.focus();
                    ta.select();
                    try {
                      document.execCommand("copy");
                    } catch {}
                    document.body.removeChild(ta);
                  }
                  toast({ title: "Copied to clipboard" });
                } catch {}
              }}
            >
              Copy
            </Button>
          </div>
          <DialogFooter>
            <Button
              variant="default"
              className={BUTTON_PRIMARY_CLASSES}
              onClick={() => setNewFullKey(null)}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;
